import { ErrorState } from '@playforge/ui';
import {
  Badge,
  BlockStack,
  Button,
  Card,
  InlineStack,
  List,
  Page,
  ProgressBar,
  Text,
  TextField,
} from '@shopify/polaris';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient, ApiError } from '../lib/api-client';
import { markOnboardingComplete } from '../lib/onboarding-state';
import type { MerchantProfile, UpdateMerchantProfile } from '../lib/types';

const STEPS = ['Welcome', 'Store info', 'Choose a plan', 'Finish'] as const;

export function WelcomeWizard(): JSX.Element {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [storeName, setStoreName] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const progress = ((step + 1) / STEPS.length) * 100;

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const saveStoreInfo = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const patch: UpdateMerchantProfile = {};
      if (storeName) patch.storeName = storeName;
      if (email) patch.email = email;
      if (Object.keys(patch).length > 0) {
        await apiClient.put<MerchantProfile>('/merchant/profile', patch);
      }
      next();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save store info');
    } finally {
      setBusy(false);
    }
  };

  const finish = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await apiClient.post<MerchantProfile>('/merchant/onboarding/complete');
      // Signal the gate (which won't refetch the profile) so it doesn't bounce
      // us back to /welcome on the next render.
      markOnboardingComplete();
      navigate('/');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not complete onboarding');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Page title="Welcome to PlayForge" subtitle={STEPS[step] ?? ''}>
      <BlockStack gap="500">
        <ProgressBar progress={progress} size="small" />
        {error ? <ErrorState message={error} /> : null}

        <Card>
          {step === 0 ? (
            <BlockStack gap="300">
              <Text as="h2" variant="headingMd">
                Turn your store into a game
              </Text>
              <Text as="p">
                PlayForge helps you launch interactive campaigns — Spin Wheel, Scratch Card and
                more — to grow engagement, emails and revenue. Let&apos;s get you set up.
              </Text>
              <InlineStack align="end">
                <Button variant="primary" onClick={next}>
                  Get started
                </Button>
              </InlineStack>
            </BlockStack>
          ) : null}

          {step === 1 ? (
            <BlockStack gap="300">
              <Text as="h2" variant="headingMd">
                Tell us about your store
              </Text>
              <TextField
                label="Store name"
                value={storeName}
                onChange={setStoreName}
                autoComplete="off"
              />
              <TextField
                label="Contact email"
                type="email"
                value={email}
                onChange={setEmail}
                autoComplete="email"
              />
              <InlineStack align="space-between">
                <Button onClick={back}>Back</Button>
                <Button variant="primary" loading={busy} onClick={() => void saveStoreInfo()}>
                  Continue
                </Button>
              </InlineStack>
            </BlockStack>
          ) : null}

          {step === 2 ? (
            <BlockStack gap="300">
              <Text as="h2" variant="headingMd">
                Choose a plan
              </Text>
              <Text as="p" tone="subdued">
                You&apos;re on the Free plan to start — upgrade anytime from Billing.
              </Text>
              <List>
                <List.Item>1 active campaign</List.Item>
                <List.Item>Email capture</List.Item>
                <List.Item>500 game plays / month</List.Item>
              </List>
              <InlineStack align="space-between">
                <Button onClick={back}>Back</Button>
                <InlineStack gap="200">
                  <Button onClick={() => navigate('/billing')}>Compare plans</Button>
                  <Button variant="primary" onClick={next}>
                    Continue with Free
                  </Button>
                </InlineStack>
              </InlineStack>
            </BlockStack>
          ) : null}

          {step === 3 ? (
            <BlockStack gap="300">
              <InlineStack gap="200" blockAlign="center">
                <Text as="h2" variant="headingMd">
                  You&apos;re all set
                </Text>
                <Badge tone="success">Ready</Badge>
              </InlineStack>
              <Text as="p">Create your first campaign to start engaging shoppers.</Text>
              <InlineStack align="space-between">
                <Button onClick={back}>Back</Button>
                <Button variant="primary" loading={busy} onClick={() => void finish()}>
                  Go to dashboard
                </Button>
              </InlineStack>
            </BlockStack>
          ) : null}
        </Card>
      </BlockStack>
    </Page>
  );
}
